import { Mic } from 'lucide-react';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ComposerVoiceStatus } from './composer-voice-status';
import { useGlobalDictation } from './global-dictation';
import { readDictationSettings } from './settings';
import { coreVoiceTransports } from './transports';
import { useVoiceInput } from './use-voice-input';

const DICTATION = 'crowdex:dictation';

/**
 * The composer's dictation: a microphone button in its toolbar, and the recording's progress
 * above the input. The text lands in the composer; sending waits while it records.
 */
export function ComposerDictation({
  scope,
  disabled,
  statusContainer,
  insertText,
  focusEditor,
  onBlocked,
}: {
  scope: string;
  disabled: boolean;
  statusContainer: HTMLElement | null;
  insertText(text: string): void;
  focusEditor(): void;
  onBlocked(id: string, blocked: boolean): void;
}) {
  const voice = useVoiceInput(scope, insertText, readDictationSettings, coreVoiceTransports);
  const busy = voice.status !== 'idle';
  // The Desktop's Dictate shortcut starts it from any app.
  useGlobalDictation(() => void voice.start(), !disabled && !busy);
  useEffect(() => {
    onBlocked(DICTATION, busy);
  }, [busy, onBlocked]);
  useEffect(() => () => onBlocked(DICTATION, false), [onBlocked]);
  const status =
    statusContainer !== null && (busy || voice.error)
      ? createPortal(
          <>
            {voice.error && (
              <p className="px-3 pb-2 text-destructive text-xs" role="alert">
                {voice.error}
              </p>
            )}
            {busy && (
              <ComposerVoiceStatus
                voice={{
                  ...voice,
                  cancel: () => {
                    voice.cancel();
                    focusEditor();
                  },
                }}
              />
            )}
          </>,
          statusContainer,
        )
      : null;
  return (
    <>
      {status}
      {!busy && (
        <button
          aria-label="Dictate message"
          className="flex size-control-sm items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
          disabled={disabled}
          onClick={() => void voice.start()}
          title="Dictate a message"
          type="button"
        >
          <Mic className="size-4" />
        </button>
      )}
    </>
  );
}
